/**
 * API 请求重试策略
 * 实现指数退避算法（Exponential Backoff）
 * 
 * 使用场景：
 * - 网络暂时不可达
 * - 服务器暂时不可用（503）
 * - 超时错误
 * 
 * 不重试场景：
 * - 客户端错误（4xx，除了 429）
 * - 认证错误（401, 403）
 */

/**
 * 重试配置
 */
export interface RetryConfig {
  /**
   * 最大重试次数（默认：3）
   */
  maxRetries?: number

  /**
   * 基础延迟时间（毫秒，默认：1000）
   */
  baseDelay?: number

  /**
   * 最大延迟时间（毫秒，默认：30000）
   */
  maxDelay?: number

  /**
   * 是否启用抖动（默认：true）
   * 抖动可以避免多个请求同时重试
   */
  enableJitter?: boolean

  /**
   * 自定义重试判断函数
   */
  shouldRetry?: (error: any, attemptNumber: number) => boolean
}

/**
 * 默认配置
 */
const DEFAULT_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 30000,
  enableJitter: true,
  shouldRetry: defaultShouldRetry,
}

/**
 * 默认重试判断逻辑
 */
function defaultShouldRetry(error: any, attemptNumber: number): boolean {
  // 已达到最大重试次数
  if (attemptNumber >= DEFAULT_CONFIG.maxRetries) {
    return false
  }

  // 网络错误（fetch 失败）
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true
  }

  // 没有响应状态码（网络错误）
  if (!error.status) {
    return true
  }

  // HTTP 状态码判断
  const status = error.status

  // 5xx 服务器错误 - 重试
  if (status >= 500 && status < 600) {
    return true
  }

  // 429 请求过于频繁 - 重试
  if (status === 429) {
    return true
  }

  // 408 请求超时 - 重试
  if (status === 408) {
    return true
  }

  // 4xx 客户端错误（除了 429）- 不重试
  if (status >= 400 && status < 500) {
    return false
  }

  // 其他情况不重试
  return false
}

/**
 * 重试策略类
 */
export class RetryStrategy {
  private config: Required<RetryConfig>

  constructor(config: RetryConfig = {}) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
    }
  }

  /**
   * 判断是否应该重试
   */
  shouldRetry(error: any, attemptNumber: number): boolean {
    return this.config.shouldRetry(error, attemptNumber)
  }

  /**
   * 计算延迟时间（指数退避 + 可选抖动）
   * 
   * 算法：delay = min(baseDelay * 2^attemptNumber, maxDelay)
   * 抖动：delay = delay * (0.5 + random(0, 0.5))
   * 
   * @param attemptNumber 重试次数（从 0 开始）
   * @returns 延迟时间（毫秒）
   */
  getDelay(attemptNumber: number): number {
    // 指数退避
    let delay = Math.min(
      this.config.baseDelay * Math.pow(2, attemptNumber),
      this.config.maxDelay
    )

    // 添加抖动（避免惊群效应）
    if (this.config.enableJitter) {
      // 随机因子：0.5 ~ 1.0
      const jitterFactor = 0.5 + Math.random() * 0.5
      delay = Math.floor(delay * jitterFactor)
    }

    return delay
  }

  /**
   * 等待指定时间
   */
  async wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * 执行带重试的请求
   * 
   * @param requestFn 请求函数
   * @param onRetry 重试回调（可选）
   * @returns 请求结果
   */
  async execute<T>(
    requestFn: () => Promise<T>,
    onRetry?: (error: any, attemptNumber: number, delay: number) => void
  ): Promise<T> {
    let lastError: any
    let attemptNumber = 0

    while (attemptNumber <= this.config.maxRetries) {
      try {
        // 执行请求
        const result = await requestFn()
        return result
      } catch (error) {
        lastError = error

        // 判断是否应该重试
        if (!this.shouldRetry(error, attemptNumber)) {
          throw error
        }

        // 已达到最大重试次数
        if (attemptNumber >= this.config.maxRetries) {
          throw error
        }

        // 计算延迟时间
        const delay = this.getDelay(attemptNumber)

        // 触发重试回调
        if (onRetry) {
          onRetry(error, attemptNumber, delay)
        }

        // 等待后重试
        await this.wait(delay)

        attemptNumber++
      }
    }

    // 理论上不会到这里，但为了类型安全
    throw lastError
  }

  /**
   * 创建带重试的 fetch 包装器
   */
  wrapFetch(
    originalFetch: typeof fetch = fetch
  ): typeof fetch {
    return async (input: RequestInfo | URL, init?: RequestInit) => {
      return this.execute(
        () => originalFetch(input, init),
        (error, attemptNumber, delay) => {
          console.warn(
            `[RetryStrategy] Request failed, retrying in ${delay}ms (attempt ${attemptNumber + 1}/${this.config.maxRetries})`,
            { error, url: input }
          )
        }
      )
    }
  }
}

/**
 * 创建默认重试策略实例
 */
export const defaultRetryStrategy = new RetryStrategy()

/**
 * 辅助函数：创建带超时的 Promise
 */
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutError?: Error
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(
        () => reject(timeoutError || new Error(`Request timeout after ${timeoutMs}ms`)),
        timeoutMs
      )
    ),
  ])
}

/**
 * 辅助函数：创建可取消的 Promise
 */
export function makeCancelable<T>(promise: Promise<T>): {
  promise: Promise<T>
  cancel: () => void
} {
  let isCanceled = false

  const wrappedPromise = new Promise<T>((resolve, reject) => {
    promise
      .then((value) => {
        if (!isCanceled) {
          resolve(value)
        }
      })
      .catch((error) => {
        if (!isCanceled) {
          reject(error)
        }
      })
  })

  return {
    promise: wrappedPromise,
    cancel: () => {
      isCanceled = true
    },
  }
}

export default RetryStrategy
